import { ColumnCenter } from 'components/Column'
import Modal from 'components/Modal'
import Row, { RowBetween } from 'components/Row'
import { ReactNode, useEffect } from 'react'
import { AlertTriangle, ChevronLeft } from 'react-feather'
import styled from 'styled-components'
import { CloseIcon } from 'theme'

import StepLine, { StepLineProps } from '.'
import Step from './common/Step'
import useAllowStepLineStateChange from './context/hooks/useAllowStepLineStateChange'
import { useStepLineState, useStepLineStateClean } from './context/StepLineContext'

type StepLineModalProps = {
  onDismiss: () => void
  isOpen: boolean
  header?: ReactNode
  onStart?: () => void
  onClose?: () => void
  disableDismiss?: boolean
  hideCloseIcon?: boolean
  simulationError?: string
} & StepLineProps

const BackButton = styled(ChevronLeft)`
  opacity: 0.8;
  &:hover {
    cursor: pointer;
    opacity: 0.5;
  }
  margin-right: 4px;
`

const ErrorContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 20px;
  min-height: 150px;
  margin: 16px 0 0;
  background-color: rgba(34, 36, 54, 0.95);
  border-radius: 8px;
  border: 1px solid #ff5252;
`

const ErrorIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`

const ErrorMessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  font-weight: 500;
  font-size: 16px;
  padding: 8px 16px;
  max-width: 80%;
`

const ErrorMessageText = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  color: #ff494a;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
`

const StepLineModal = ({
  simulationError,
  isOpen,
  onDismiss,
  onStart,
  onClose,
  header,
  disableDismiss = false,
  hideCloseIcon = false,
  ...rest
}: StepLineModalProps) => {
  const clean = useStepLineStateClean()
  const { __currentStep: currentStep } = useStepLineState<{ __currentStep: Step }>()
  const { __prevStep: prevStep } = useStepLineState<{ __prevStep: Step }>()
  const setAllow = useAllowStepLineStateChange()
  useEffect(() => {
    clean()

    if (isOpen) {
      setAllow(true)
      onStart && onStart()
    } else {
      setAllow(false)
      onClose && onClose()
    }
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} disableDismiss={disableDismiss}>
      <ColumnCenter gap="8px">
        <RowBetween width="100%">
          <Row>
            {prevStep?.onBack && <BackButton onClick={prevStep.onBack} size={19} />}
            <div>{currentStep?.title || header}</div>
          </Row>
          {!hideCloseIcon && <CloseIcon onClick={onDismiss} data-cy="confirmation-close-icon" />}{' '}
        </RowBetween>
        {!simulationError || simulationError === '' ? (
          <StepLine {...rest} />
        ) : (
          <ErrorContainer>
            <ErrorIconWrapper>
              <AlertTriangle color="#FF494A" scale={5} size={48} />
            </ErrorIconWrapper>
            <ErrorMessageWrapper>
              <ErrorMessageText>{simulationError || 'Simulation Failed'}</ErrorMessageText>
            </ErrorMessageWrapper>
          </ErrorContainer>
        )}
      </ColumnCenter>
    </Modal>
  )
}

export default StepLineModal
